import {List} from 'immutable';

import createSelector from 'web-app/util/custom-create-selector';
import InstitutionBehaviors from 'web-app/behaviors/institution-behavior-ids';
import {type RootState} from 'web-app/react/redux/main-reducer';

import {hasBehavior} from './helpers';

export const getInstitutions = (state: RootState) => state.entities.institutions;

const makeGetInstitutionByProps = () =>
    createSelector(
        getInstitutions,
        (state, props: {institutionId: string}) => props.institutionId,
        (institutions, institutionId) => {
            return institutions.get(institutionId);
        },
    );

export const getInstitutionByProps = makeGetInstitutionByProps();

export const getInstitutionOpeningHours = createSelector(getInstitutionByProps, institution =>
    institution ? institution.openingHours : null,
);

export const institutionTitle = createSelector(getInstitutionByProps, institution =>
    institution ? institution.title : undefined,
);

export const institutionImageSmall = createSelector(getInstitutionByProps, institution =>
    institution && institution.image ? institution.image.small : undefined,
);

/**
 * @deprecated use the useStatSettings().openingHours  instead
 */
export const canEditInsitutionOpeningHours = createSelector(
    getInstitutionByProps,
    institution => institution && institution.openingHoursBehaviors.includes(InstitutionBehaviors.CanEditOpeningHours),
);

export const getGroups = createSelector(
    getInstitutionByProps,
    (institution): List<any> => (institution ? institution.allGroups : List()),
);

export const isFetchingGroups = createSelector(getInstitutionByProps, institution =>
    Boolean(institution?.fetchingGroups),
);

export const didFetchGroups = createSelector(getInstitutionByProps, institution =>
    Boolean(institution?.didFetchGroups),
);

export const famlyId = createSelector(getInstitutionByProps, (institution): string | undefined => institution?.famlyId);

export const hasExpectedChildren = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowExpectedChildren),
);

export const canCreateGroup = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanCreateGroup),
);

export const showStaffQualifications = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowStaffQualifications),
);

export const canSetupGroupRules = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanSetupGroupRules),
);

export const hasDirectDebit = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.EnableDirectDebit),
);

export const canManageKibig = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanManageKibigWeb),
);

export const canManageGenericFunding = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanManageGenericFunding),
);

export const canUpdateMealTypes = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanUpdateMealTypes),
);

export const showOccupancyPlanner = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowOccupancyPlanner),
);

export const canListDirectDebitFiles = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanListDirectDebitFiles),
);

export const canEditInstitutionSettings = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanEditInstitutionSettings),
);

export const canManageCustomStatuses = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanManageCustomStatuses),
);

export const canManageAgeGroups = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanManageAgeGroups),
);

export const canCreateChild = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanCreateChild),
);

export const canCreateChildBehavior = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.CanCreateChild),
);

export const canInviteParents = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanInviteParents),
);

export const canCreateInstitutionRoles = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanCreateInstitutionRoles),
);

export const showGroupChart = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowGroupChart),
);

export const showInstitutionChart = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowInstitutionChart),
);

export const showSignInButton = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowSignInButton),
);

export const showSwitchRoomButton = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowSwitchRoomButton),
);

export const showChildSickButton = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowChildSickButton),
);

export const canAddDiaperToiletAccident = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanAddDiaperToiletAccident),
);

export const canCheckChildrenInOrOut = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanCheckChildrenInOrOut),
);

export const canSendPaymentReminders = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanSendPaymentReminders),
);

export const canManagePaymentReminderTemplates = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanManagePaymentReminderTemplates),
);

// Covers all actions that mutate a lesson plan, i.e. create, edit, publish, unpublish, etc.
export const canMutateLessonPlans = createSelector(getInstitutionByProps, institution => {
    return Boolean(
        institution?.behaviors.behaviorForId(InstitutionBehaviors.ShowLessonPlansApp)?.payload?.canCreateLessonPlans,
    );
});

export const usePincodeCheckin = createSelector(
    getInstitutionByProps,
    institution => institution?.usePincodeCheckin ?? false,
);

export const institutionCountry = createSelector(getInstitutionByProps, institution => institution?.country);

export const canAddMultipleFoodItems = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanAddMultipleFoodItems),
);

export const canAddWeekendMeals = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanAddWeekendMeals),
);

export const canUseDiscountPresets = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanUseDiscountPresets),
);

export const canListDeposits = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanListDeposits),
);

export const canManagePaymentsAccount = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanManagePaymentsAccount),
);

export const canSupportAutomaticPayments = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanSupportAutomaticPayments),
);

export const canSetAccountForSessionsAndProducts = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanSetAccountForSessionsAndProducts),
);

export const canManageInstitutionRoles = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanManageInstitutionRoles),
);

export const showBillPayerBankDetails = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.ShowBillPayerBankDetails),
);

/**
 * This selector was ported from Ember modal "Institution" @ app/models/institution.js
 */
export const hasPlannedCheckout = createSelector(getInstitutionByProps, institution =>
    institution?.features.includes('PLANNED_CHECKOUT'),
);

export const institutionTimezone = createSelector(getInstitutionByProps, institution => institution?.timezone);

export const canEditInstitutionRoles = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanEditInstitutionRoles),
);

export const showFamlyPayAppBehavior = createSelector(getInstitutionByProps, institution => {
    return institution && institution.behaviors
        ? institution.behaviors.behaviorForId(InstitutionBehaviors.ShowFamlyPayApp)
        : undefined;
});

export const showBillPayerSensitiveInfo = createSelector(getInstitutionByProps, institution => {
    return hasBehavior(institution, InstitutionBehaviors.ShowBillPayerSensitiveInfo);
});

export const showFamlyPayApp = createSelector(showFamlyPayAppBehavior, showFamlyPayAppBehavior => {
    return Boolean(showFamlyPayAppBehavior);
});

export const showReportedToForAccidentReports = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowReportedToForAccidentReports),
);

export const showImportModule = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowImportModule),
);

export const canExportFromKitaPlaner = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanExportFromKitaPlaner),
);

export const canEditPrivacyLink = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanEditPrivacyLink),
);

export const hasBehaviorForShowingAccounts = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowAccountingSettings),
);

export const showInvoiceSettingsOrganizationNo = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.ShowInvoiceSettingsOrganizationNo),
);

export const canCreatePayments = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanCreatePayment),
);

export const canCreateEmployee = createSelector(getInstitutionByProps, institution =>
    hasBehavior(institution, InstitutionBehaviors.CanCreateEmployee),
);

export const canManageBillPayerReferences = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.CanManageBillPayerReferences),
);

export const canManageCheckinStatements = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.CanManageCheckinStatements),
);

export const canSeeCheckinStatements = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.CanSeeCheckinStatements),
);

export const getInstituitonLocale = createSelector(getInstitutionByProps, institution =>
    institution ? institution.locale : null,
);

export const showStaffRotasTrialData = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.ShowStaffRotasTrialData),
);

export const canScanDocuments = createSelector(
    getInstitutionByProps,
    institution => institution?.behaviors?.includes(InstitutionBehaviors.CanScanDocuments) ?? false,
);

export const canViewPaymentReminders = createSelector(
    getInstitutionByProps,
    institution => institution?.behaviors?.includes(InstitutionBehaviors.CanViewPaymentReminders) ?? false,
);
export const enableSafeguarding = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.EnableSafeguarding),
);

export const enableMedicationForms = createSelector(
    getInstitutionByProps,
    institution =>
        institution?.behaviors?.behaviorForId(InstitutionBehaviors.ShowHealthAndSafetyApp)?.payload
            ?.enableMedicationForms ?? false,
);

export const showMissingIntegrationWarningBehavior = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.ShowMissingIntegrationWarning),
);

export const showMealRegistrationInOverview = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.ShowMealRegistrationInOverview),
);

export const showListFilterAppBehavior = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.ShowListFilterApp),
);

export const showFamlyReportAppBehavior = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.ShowFamlyReportApp),
);

export const canEditTag = createSelector(getInstitutionByProps, institution =>
    institution?.behaviors?.behaviorForId(InstitutionBehaviors.CanEditTag),
);
